/**
 * The type layer IS the credibility contract.
 *
 * Three rules are enforced structurally rather than by discipline:
 *   1. Numbers enter the system only as `Input`. Metrics are always derived.
 *   2. Sources hang off Inputs, never off Metrics. A metric's citation list is
 *      computed by walking its expression to the inputs it actually depends on,
 *      so "these are the sources behind this number" is a guarantee.
 *   3. A value that is not public is `Undisclosed`, never 0.
 */

// ---------------------------------------------------------------- numbers

/** lo/mid/hi. `mid` is the central estimate, not necessarily the midpoint. */
export interface Range {
  lo: number;
  mid: number;
  hi: number;
}

/**
 * A value that exists but is not public. Rendering this as zero would be a
 * fabrication, so the schema makes the honest option the easy one.
 */
export interface Undisclosed {
  undisclosed: true;
  /** e.g. "Contained in the ESA at MPUC docket E-002/M-26-170; not public." */
  note: string;
  /** Who was asked, and when, if anyone. */
  requestedFrom?: string;
}

export type Num = number | Range;
export type InputValue = Num | Undisclosed;

export function isRange(v: unknown): v is Range {
  return typeof v === 'object' && v !== null && 'lo' in v && 'mid' in v && 'hi' in v;
}

export function isUndisclosed(v: unknown): v is Undisclosed {
  return typeof v === 'object' && v !== null && (v as Undisclosed).undisclosed === true;
}

export function mid(v: Num): number {
  return isRange(v) ? v.mid : v;
}

export function toRange(v: Num): Range {
  return isRange(v) ? v : { lo: v, mid: v, hi: v };
}

// ---------------------------------------------------------------- units

export type UnitId =
  | 'usd'
  | 'usd_per_year'
  | 'count'
  | 'ratio'
  | 'percent'
  | 'year'
  | 'years'
  | 'mw'
  | 'mwh'
  | 'mwh_per_year'
  | 'lb_per_mwh'
  | 'short_tons'
  | 'short_tons_per_year'
  | 'gallons_per_day'
  | 'million_gallons_per_year'
  | 'acres'
  | 'dba'
  | 'hours'
  | 'job_years'
  | 'teacher_years'
  | 'apprentice_years'
  | 'usd_per_teacher_year'
  | 'usd_per_job_year'
  | 'hours_per_job_year'
  | 'hours_per_apprentice_year'
  // Conversion constants. Giving these real dimensions is what lets the
  // validator prove that MW × h/yr × lb/MWh × ton/lb really is tons/yr.
  | 'hours_per_year'
  | 'short_tons_per_lb'
  | 'months_per_year'
  | 'people'
  | 'customers'
  | 'usd_per_mwh'
  | 'usd_per_customer_month'
  | 'sq_ft'
  | 'dimensionless';

// ---------------------------------------------------------------- inputs

/**
 * How much weight a number can bear.
 *  - `fact`       stated in a public document; not user-adjustable
 *  - `policy`     a statutory or tariff constant (mill rate, class rate)
 *  - `estimate`   our derivation from public data
 *  - `assumption` genuinely contestable; drives a slider
 */
export type InputKind = 'fact' | 'policy' | 'estimate' | 'assumption';

/**
 * Provenance strength. Rolls up through a metric as the WORST value in its
 * dependency closure — one placeholder input makes the whole metric a
 * placeholder, which is the correct behaviour.
 */
export type Confidence = 'documented' | 'derived' | 'modeled' | 'placeholder';

export const CONFIDENCE_ORDER: Confidence[] = [
  'documented',
  'derived',
  'modeled',
  'placeholder',
];

export interface ContestedClaim {
  claim: string;
  counterClaim: string;
  sourceIds: string[];
}

export interface Adjustable {
  min: number;
  max: number;
  step: number;
  sliderLabel: string;
  /** WHY these bounds are the defensible range. Required — an unexplained
   *  slider range is an invitation to screenshot an extreme. */
  rationale: string;
}

export interface Input {
  /** snake_case. This is the symbol used in metric expressions. */
  id: string;
  label: string;
  /** Plain-language name for non-analyst mode. */
  plainLabel: string;
  unit: UnitId;
  value: InputValue;
  kind: InputKind;
  /** How precisely this is actually known. Drives output rounding. */
  sigFigs: number;
  /** ISO date the VALUE refers to — not the source's publication date. */
  asOf: string;
  sourceIds: string[];
  confidence: Confidence;
  /** One or two plain sentences. Shown in the provenance drawer. */
  note?: string;
  /** Values that differ by scenario. This is where scenarios do their work. */
  scenarioValues?: Partial<Record<string, Num>>;
  adjustable?: Adjustable;
  /** Where a number is publicly disputed, both sides live here. */
  contested?: ContestedClaim[];
}

// ---------------------------------------------------------------- scenarios

export interface ScenarioParam {
  id: string;
  label: string;
  min: number;
  max: number;
  step: number;
  default: number;
  unit: UnitId;
  /** What makes this range defensible. */
  sourceIds: string[];
}

export interface Scenario {
  id: string;
  label: string;
  plainLabel: string;
  summary: string;
  /**
   * REQUIRED. What happens instead. "Delayed" and "elsewhere" are undefined
   * without a stated alternative, and every number in those columns is
   * meaningless until the alternative is written down.
   */
  counterfactual: string;
  inputOverrides: Record<string, Num>;
  params?: ScenarioParam[];
  sourceIds: string[];
  /** Render with the placeholder hatch and a warning. */
  lowConfidence?: boolean;
}

// ---------------------------------------------------------------- metrics

export type Category = 'economic' | 'civic' | 'social';
export type Polarity = 'more-is-better' | 'less-is-better' | 'contested';
export type MetricStatus = 'live' | 'placeholder' | 'planned';

export interface MetricSeries {
  /** Input id holding the horizon in years. */
  horizonYearsInput: string;
  /** Expression evaluated per year; may reference `t` (years elapsed, 0-based). */
  perYearExpr: string;
}

export interface Metric {
  id: string;
  category: Category;
  label: string;
  plainLabel: string;
  /** The question this metric answers, in plain words. */
  question: string;
  unit: UnitId;
  polarity: Polarity;

  /** Executable AND printable, from one string. Single source of truth. */
  expr: string;
  series?: MetricSeries;

  /** Omit = applies to all scenarios. */
  appliesTo?: string[];

  methodology: string;
  /** REQUIRED, min 1. What this number does NOT capture. */
  caveats: string[];
  /** Metrics this double-counts with. Never summable together. */
  overlapsWith?: string[];
  comparatorIds?: string[];
  displayAs: 'value' | 'delta' | 'both';
  status: MetricStatus;
  /**
   * Set when a metric's honest reading is unfavourable to the project.
   *
   * A record, not a rendering instruction. Such a finding is published in full
   * like any other, and is NOT badged as unflattering — a page that points at
   * its own fairness is arguing with a critic rather than presenting evidence.
   * This flag exists so it is always clear which finding that is, and so a
   * future edit cannot quietly drop it without the change being visible here.
   */
  cutsAgainst?: boolean;
}

// ---------------------------------------------------------------- sources

export type SourceKind =
  | 'filing'
  | 'court'
  | 'agency-data'
  | 'statute'
  | 'news'
  | 'academic'
  | 'company-statement'
  | 'local-government';

export interface Source {
  id: string;
  title: string;
  publisher: string;
  url: string;
  /** Captured at ingest, not later. Company announcements get edited. */
  archiveUrl?: string;
  /** Committed snapshot under projects/<slug>/sources/ for load-bearing docs. */
  localSnapshot?: string;
  publishedAt?: string;
  retrievedAt: string;
  kind: SourceKind;
  /** "Table 3, p.14" / "Order at 10" */
  locator?: string;
  /** <= 25 words, the exact sentence backing the number. */
  quote?: string;
  notes?: string;
}

// ---------------------------------------------------------------- comparators

export interface Comparator {
  id: string;
  plainLabel: string;
  /** An Input, with its own citation. The divisor is never unsourced. */
  unitCostInputId?: string;
  /**
   * A divisor that is not a single input — the cost of a teacher-year is a
   * salary times a benefit load, and writing it as one number would hide the
   * assumption inside it. Parsed, unit-checked and provenance-walked exactly
   * like a metric expression, so the citations behind a comparison are as
   * traceable as the citations behind the figure it compares.
   *
   * Exactly one of `unitCostInputId` or `unitCostExpr` must be set.
   */
  unitCostExpr?: string;
  /** "teacher-years", "golf courses", "irrigated acres" */
  verb: string;
  /** Which metric units this comparator is valid against. Enforced. */
  appliesToUnit: UnitId;
  /** Measured actual vs permitted maximum. Mixing these is the classic error. */
  basis: 'measured-actual' | 'permitted-maximum' | 'budgeted' | 'modeled';
  note?: string;
}

// ---------------------------------------------------------------- pack

export interface ProjectMeta {
  slug: string;
  name: string;
  place: string;
  /** Shown in the header. Kept short. */
  tagline: string;
  /** Bumped on every substantive number change; encoded in shared URLs. */
  dataVersion: number;
  lastUpdated: string;
  defaultScenario: string;
  publisher: string;
  publisherNote: string;
}

export interface Pack {
  meta: ProjectMeta;
  inputs: Input[];
  scenarios: Scenario[];
  metrics: Metric[];
  comparators: Comparator[];
  sources: Source[];
  narrative: Record<string, string>;
  /** Sources for figures cited in prose rather than computed by a metric. */
  narrativeCitations?: Record<string, string[]>;
}

// ---------------------------------------------------------------- results

export interface Step {
  label: string;
  value: Range;
  unit: UnitId;
}

export interface MetricResult {
  metricId: string;
  scenarioId: string;
  value: Range | Undisclosed;
  unit: UnitId;
  sigFigs: number;
  confidence: Confidence;
  /** Inputs this metric actually depends on, transitively. */
  inputClosure: string[];
  /** Sources backing those inputs, deduplicated, order-stable. */
  sourceIds: string[];
  /** Per-year values when the metric declares a series. */
  series?: Range[];
  /** True when any input in the closure is undisclosed. */
  blocked: boolean;
}

export function worstConfidence(cs: Confidence[]): Confidence {
  let worst: Confidence = 'documented';
  for (const c of cs) {
    if (CONFIDENCE_ORDER.indexOf(c) > CONFIDENCE_ORDER.indexOf(worst)) worst = c;
  }
  return worst;
}
